// Reusable multimodal catalog input (2026-10-20, SPEC-AIRPORTS-IATA-DISTANCES):
// a text field + native <datalist> backed by the LOCAL catalog — airports
// (mode 'air', 3-letter IATA codes) or train stations (mode 'rail', numeric
// UIC codes). Pattern copied from CountryInput: zero dependencies.
//
// Storage = the catalog CODE (IATA or UIC) in the existing column. The server
// refuses any other value on save (400 — server/lib/catalog-codes.js); here a
// value that matches nothing is refused at BLUR with an inline message and is
// never pushed to the parent. A value that matches a catalog row is normalized
// to its uppercase code and displayed readably ("GVA — Genève (Geneva
// Airport)").

import { useEffect, useRef, useState } from 'react';
import { apiSearchAirports, apiSearchStations, type AirportDTO, type StationDTO } from '../api';

export type AirportInputMode = 'air' | 'rail';

interface CatalogEntry {
  code: string;
  label: string;
}

function toEntries(mode: AirportInputMode, airs: AirportDTO[], rails: StationDTO[]): CatalogEntry[] {
  return mode === 'air'
    ? airs.map((a) => ({ code: a.iata, label: `${a.iata} — ${a.city || a.name} (${a.name})` }))
    : rails.map((s) => ({ code: s.uic, label: `${s.uic} — ${s.name}${s.country ? ` (${s.country})` : ''}` }));
}

interface AirportInputProps {
  value: string;
  onChange: (v: string) => void;
  /** 'air' = airports catalog (IATA), 'rail' = stations catalog (UIC). */
  mode?: AirportInputMode;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
}

export default function AirportInput({ value, onChange, mode = 'air', placeholder = '', className = 'form-input', disabled = false }: AirportInputProps) {
  const [text, setText] = useState(value || '');
  const [entries, setEntries] = useState<CatalogEntry[]>([]);
  const [error, setError] = useState<string | null>(null);
  const listId = useRef(`airport-list-${Math.random().toString(36).slice(2, 8)}`).current;
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Keep the text in sync when the parent value changes externally
  // (e.g. profile switch) — but not while this field is being edited.
  useEffect(() => {
    setText((cur) => (document.activeElement && (document.activeElement as HTMLElement).dataset.airportList === listId ? cur : (value || '')));
  }, [value, listId]);

  useEffect(() => () => { if (debounceRef.current) clearTimeout(debounceRef.current); }, []);

  const search = (q: string) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      if (!q || q.trim().length < 2) { setEntries([]); return; }
      try {
        if (mode === 'air') {
          const rows = await apiSearchAirports(q);
          setEntries(toEntries('air', rows, []));
        } else {
          const rows = await apiSearchStations(q);
          setEntries(toEntries('rail', [], rows));
        }
      } catch { /* catalog lookup is best-effort; blur validation still guards */ }
    }, 250);
  };

  const commit = (raw: string) => {
    const v = raw.trim();
    if (!v) { setError(null); onChange(''); return; }
    // Exact code match (case-insensitive) against the fetched catalog rows.
    const hit = entries.find((e) => e.code.toUpperCase() === v.toUpperCase())
      // A stored valid code (reloaded from the server) stays valid even
      // without suggestions in hand — the server re-validates on save.
      || (v.toUpperCase() === String(value || '').toUpperCase() ? { code: v.toUpperCase(), label: v } : null);
    if (hit) {
      setError(null);
      setText(hit.code);
      onChange(hit.code);
    } else {
      setError(mode === 'air'
        ? `Unknown airport "${v}" — pick one from the list (IATA code, city or name)`
        : `Unknown station "${v}" — pick one from the list (UIC code or name)`);
    }
  };

  return (
    <>
      <input
        className={className}
        type="text"
        list={listId}
        data-airport-list={listId}
        value={text}
        placeholder={placeholder}
        disabled={disabled}
        onChange={(e) => { setText(e.target.value); search(e.target.value); }}
        onBlur={(e) => commit(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); commit((e.target as HTMLInputElement).value); } }}
      />
      <datalist id={listId}>
        {entries.map((e) => (
          <option key={e.code} value={e.code}>{e.label}</option>
        ))}
      </datalist>
      {error && (
        <div style={{ color: 'var(--danger)', fontSize: 12, marginTop: 4 }} role="alert">{error}</div>
      )}
    </>
  );
}
